import React, { useEffect, useRef, useState, forwardRef, useCallback } from 'react';
import Plot from 'react-plotly.js';

const LazyPlot = forwardRef(({ data, layout, height = 300, config }, innerRef) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [forceRenderAll, setForceRenderAll] = useState(false);

  // --- 印刷モードの検知 ---
  useEffect(() => {
    const mediaQueryList = window.matchMedia('print');
    
    const handler = (e) => {
      setForceRenderAll(e.matches);
    };
    
    mediaQueryList.addEventListener('change', handler);
    
    // 初期判定
    if (mediaQueryList.matches) setForceRenderAll(true);
    
    return () => mediaQueryList.removeEventListener('change', handler);
  }, []);

  // --- Intersection Observer による表示の検知 ---
  useEffect(() => {
    if (forceRenderAll) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
      observer.disconnect();
    };
  }, [forceRenderAll]); // forceRenderAll が変わったときにのみオブザーバーを再設定

  const shouldRender = forceRenderAll || isVisible;

  // --- 印刷時2D代替ロジック ---
  const getProcessedPlotProps = useCallback(() => {
    if (!forceRenderAll) {
      // 通常表示時はそのままのデータとレイアウトを使用
      return {
        currentData: data,
        currentLayout: { ...layout, autosize: true }
      };
    }

    // 印刷時はデータを2Dに変換し、レイアウトから3D設定を削除
    const printData = data.map(trace => {
      if (trace.type === 'scatter3d') {
        return {
          ...trace,
          type: 'scatter', // 2D散布図に切り替え
          mode: trace.mode ? trace.mode.replace('3d', '') : 'markers', // 3dサフィックスを削除、デフォルトはmarkers
        };
      }
      // 他の3Dタイプ（surface, mesh3dなど）も必要に応じて変換ロジックを追加
      return trace;
    });

    const { scene, ...restOfLayout } = layout;
    const printLayout = {
      ...restOfLayout, // 元のレイアウト設定を引き継ぐ
      xaxis: layout.scene?.xaxis ? { ...layout.scene.xaxis } : {}, 
      yaxis: layout.scene?.yaxis ? { ...layout.scene.yaxis } : {},
      autosize: true,
    };
    return {
      currentData: printData,
      currentLayout: printLayout
    };
  }, [data, layout, forceRenderAll]);

  const { currentData, currentLayout } = getProcessedPlotProps();

  return (
    <div ref={containerRef} style={{ height }}>
      {shouldRender ? (
        <Plot
          // 印刷時と通常時でキーを分けることで、Plotコンポーネントを再構築させる
          // これにより、3D -> 2D へのグラフタイプ変更が確実に適用される
          key={forceRenderAll ? 'plotly-print-2d' : 'plotly-normal-3d'} 
          ref={innerRef}
          data={currentData}
          layout={currentLayout}
          config={config} 
          style={{ width: '100%', height: '100%' }}
        />
      ) : (
        <div style={{ height, background: '#fff' }} />
      )}
    </div>
  );
});

export default LazyPlot;